import { useEffect, useRef } from 'react'
import { io, Socket } from 'socket.io-client'
import { useAuth } from '../context/AuthContext'

let socket: Socket | null = null
let isConnecting = false

export const useRealtimeNotifications = () => {
  const { isAdmin, user } = useAuth()
  const hasInitialized = useRef(false)
  const hasRequestedPermission = useRef(false)

  useEffect(() => {
    if (hasInitialized.current || !isAdmin() || !user) {
      return
    }

    hasInitialized.current = true
    
    if (isConnecting || socket?.connected) {
      console.log('Socket.IO ja esta conectado')
      return
    }

    console.log('Inicializando Socket.IO para admin:', user.nome)
    isConnecting = true

    const apiUrl = window.location.origin
    socket = io(apiUrl, {
      path: '/socket.io/',
      transports: ['polling'],
      reconnectionDelay: 1000,
      reconnectionAttempts: 10,
      withCredentials: true,
      secure: true,
      autoConnect: true
    })

    socket.on('connect', () => {
      isConnecting = false
      console.log('Socket.IO conectado! ID:', socket?.id)
      
      if (!hasRequestedPermission.current && 'Notification' in window) {
        hasRequestedPermission.current = true
        
        if (Notification.permission === 'default') {
          setTimeout(() => {
            Notification.requestPermission().then(permission => {
              if (permission === 'granted') {
                console.log('Permissao de notificacoes concedida!')
                // Teste de notificação imediatamente após permissão
                try {
                  const testNotif = new Notification('NexaOS - Notificacoes Ativas', {
                    body: 'Voce recebera notificacoes sobre ordens de servico',
                    requireInteraction: true,
                    tag: 'test-notification'
                  })
                  console.log('[DEBUG] Notificação de teste criada:', testNotif)
                } catch (err) {
                  console.error('[ERRO] Falha na notificação de teste:', err)
                }
              }
            })
          }, 3000)
        } else if (Notification.permission === 'granted') {
          // Se já tem permissão, fazer teste imediato
          console.log('[DEBUG] Permissão JÁ concedida, testando notificação...')
          setTimeout(() => {
            try {
              const testNotif = new Notification('🔔 NexaOS - Teste de Notificação', {
                body: 'Se você está vendo isto, as notificações estão funcionando!',
                requireInteraction: true,
                tag: 'connection-test',
                silent: false
              })
              console.log('[DEBUG] Notificação de teste ao conectar:', testNotif)
              
              testNotif.onclick = () => {
                console.log('[DEBUG] Notificação de teste foi clicada!')
                testNotif.close()
              }
              
              setTimeout(() => testNotif.close(), 10000)
            } catch (err) {
              console.error('[ERRO] Falha no teste de notificação:', err)
            }
          }, 1000)
        }
      }
    })

    socket.on('connect_error', (error) => {
      isConnecting = false
      console.error('Erro Socket.IO:', error.message)
    })

    socket.on('disconnect', (reason) => {
      isConnecting = false
      console.log('Socket.IO desconectado:', reason)
    })

    socket.on('ordemServico:created', (data) => {
      console.log('Nova OS via Socket.IO:', data)
      
      if (Notification.permission === 'granted') {
        console.log('[DEBUG] Tentando criar notificação para OS criada...')
        const titulo = '🔔 Nova OS #' + data.numero
        const corpo = data.criadoPor + ' criou uma nova ordem de servico'
        
        try {
          const notification = new Notification(titulo, {
            body: corpo,
            tag: 'os-' + data.id,
            requireInteraction: true,
            silent: false,
            vibrate: [200, 100, 200]
          })
          
          console.log('[DEBUG] Notificação criada com sucesso!', notification)
          
          notification.onshow = () => {
            console.log('[DEBUG] Notificação MOSTRADA na tela!')
          }
          
          notification.onclick = () => {
            console.log('[DEBUG] Notificação foi CLICADA!')
            window.focus()
            window.location.href = '/ordens-servico/' + data.id
            notification.close()
          }
          
          notification.onerror = (err) => {
            console.error('[ERRO] Erro na notificação:', err)
          }
          
          notification.onclose = () => {
            console.log('[DEBUG] Notificação foi FECHADA')
          }
          
          setTimeout(() => notification.close(), 15000)
        } catch (error) {
          console.error('[ERRO] Falha ao criar notificação:', error)
        }
      } else {
        console.warn('[DEBUG] Permissão negada. Estado:', Notification.permission)
      }
    })

    socket.on('ordemServico:stateChanged', (data) => {
      console.log('Estado OS alterado via Socket.IO:', data)
      
      if (Notification.permission === 'granted') {
        console.log('[DEBUG] Tentando criar notificação para mudança de estado...')
        const titulo = '🔔 OS #' + data.numero + ' - Estado Alterado'
        const corpo = data.alteradoPor + ' alterou para  + data.estadoNovo + '
        
        try {
          const notification = new Notification(titulo, {
            body: corpo,
            tag: 'os-' + data.id + '-state-' + Date.now(),
            requireInteraction: true,
            silent: false,
            vibrate: [200, 100, 200]
          })
          
          console.log('[DEBUG] Notificação criada com sucesso!', notification)
          
          notification.onshow = () => {
            console.log('[DEBUG] ✅ Notificação MOSTRADA na tela!')
          }
          
          notification.onclick = () => {
            console.log('[DEBUG] Notificação foi CLICADA!')
            window.focus()
            window.location.href = '/ordens-servico/' + data.id
            notification.close()
          }
          
          notification.onerror = (err) => {
            console.error('[ERRO] Erro na notificação:', err)
          }
          
          notification.onclose = () => {
            console.log('[DEBUG] Notificação foi FECHADA')
          }
          
          setTimeout(() => notification.close(), 15000)
        } catch (error) {
          console.error('[ERRO] Falha ao criar notificação:', error)
        }
      } else {
        console.warn('[DEBUG] Permissão negada. Estado:', Notification.permission)
      }
    })

    return () => {
      console.log('Cleanup Socket.IO')
    }
  }, [])

  useEffect(() => {
    if (!isAdmin() && socket?.connected) {
      console.log('Desconectando Socket.IO (logout)')
      socket.disconnect()
      socket = null
      hasInitialized.current = false
      hasRequestedPermission.current = false
      isConnecting = false
    }
  }, [isAdmin])
}
